import _ from 'lodash'
import React, { useState, useEffect, useContext, useLayoutEffect } from 'react'

import globalConfirmation from 'live/services/global-confirmation'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { updateModeFrontAPI } from 'plugin-codaf-risk-ext-bowtie/shared/UpdateModeFrontAPI'
import { SpecialStatus, UpdateModeMethodId } from 'plugin-codaf-risk-ext-bowtie/types/enums'

import { EditorTopBar, IError } from '../../../generic'
import { useDimensions } from '../../../hooks'
import { getBarrier, updateBarrier, getUpdateModeMethods, listBarrierTypeIcons } from '../../../services'
import { IBarrierTypeIcon, IMethod } from '../../../types'
import { checkIsValidFrequencyRange, capitalize, isNullOrUndefined } from '../../../utils'
import { barrierEditorQueryStringKey, bowtieTheme } from '../../../utils/constants'
import {
    BowtieSettingsRouteContext,
    IBowtieSettingsRouteContext,
    IQueryParamsEdit,
    IQueryParamsList
} from '../../BowtieSettingWrapper'
import { BarrierEditorSvg, BarrierEditorList, BarrierEditModal, EscalationEditModal } from './components'
import { BarrierEditorDiv } from './styles'

export type IBarrierTypes = 'BARRIER' | 'ESCALATIONFACTOR' | 'ESCALATIONBARRIER'

export type IEditorBarrier = import('../../../types').IBarrier & { uid: string; getFather?: IEditorEscalation }
export type IEditorEscalation = import('../../../types').IEscalationFactor & { uid: string; getFather?: IEditorBarrier }

export interface IUpdateMethodsOptions {
    id: number
    value: string
    label: string
}

export const BarrierEditor: React.FC = () => {
    const dimensions = useDimensions()
    const [barrierTypeIconMap] = useState<Map<number, IBarrierTypeIcon>>(new Map())
    const [showEntityModal, setShowEntityModal] = useState(false)
    const [needToUpdate, setNeedToUpdate] = useState(false)
    const [inLoading, setInLoading] = useState(true)

    const [barrier, setBarrier] = useState<IEditorBarrier>()
    const [tempItem, setTempItem] = useState<IEditorEscalation[] | IEditorBarrier[]>()
    const [focus, setFocus] = useState<IEditorBarrier | IEditorEscalation>()
    const [typeToEdit, setTypeToEdit] = useState<IBarrierTypes>()
    const [errors, setErrors] = useState<IError[]>([])
    const [updateModeMethods, setUpdateModeMethods] = useState<IUpdateMethodsOptions[]>([])

    const [saveLoading, setSaveLoading] = useState(false)

    const handleFetchBarrierTypeIconsAndStoreIntoDictionary = async (): Promise<void> => {
        const data = await listBarrierTypeIcons()
        data?.data?.forEach(el => {
            const decodedSvg = atob(el.svgImage)
            el.decodedSvgImage = decodedSvg
            barrierTypeIconMap.set(el.id, el)
        })
    }

    const isQualitativeMethod = (bar: IEditorBarrier): boolean => {
        const method = bar?.method?.method
        if (!method) return false

        return method === 'QUALITATIVE'
    }

    const mountErrorList = (bar: IEditorBarrier): IError[] => {
        let items: IError[] = []

        if (bar) {
            const barrierErrors: string[] = []

            if (!bar.name?.length) {
                barrierErrors.push(i18n('name'))
            }

            if (!bar.barrierType || !bar.barrierType.id || !bar.barrierType.name.length) {
                barrierErrors.push(i18n('barrier category'))
            }

            if (!bar.supervisor || !bar.supervisor.name.length) {
                barrierErrors.push(i18n('supervisor'))
            }

            if (bar.manualInteraction) {
                if (isQualitativeMethod(bar)) {
                    if (!bar.manualInteraction.manualQLR) {
                        barrierErrors.push(i18n('Manual Interaction QLR'))
                    } else if (!checkIsValidFrequencyRange(bar.manualInteraction.manualQLR, 0, 1, true, true)) {
                        barrierErrors.push(i18n('Manual Interaction QLR'))
                    }
                } else {
                    if (!bar.manualInteraction.manualInitialPFD) {
                        barrierErrors.push(i18n('Manual Interaction Initial PFD'))
                    } else if (!checkIsValidFrequencyRange(bar.manualInteraction.manualInitialPFD)) {
                        barrierErrors.push(i18n('Manual Interaction Initial Frequency'))
                    }
                    if (!bar.manualInteraction.manualFinalPFD) {
                        barrierErrors.push(i18n('Manual Interaction Final PFD'))
                    } else if (!checkIsValidFrequencyRange(bar.manualInteraction.manualFinalPFD)) {
                        barrierErrors.push(i18n('Manual Interaction Final PFD'))
                    }
                }

                if (!bar.manualInteraction.moc) {
                    barrierErrors.push(i18n('Manual Interaction MOC'))
                }
                if (!bar.manualInteraction.responsiblePerson) {
                    barrierErrors.push(i18n('Manual Interaction Responsible'))
                }
            }

            if (bar.barrierType?.barrierTypeSpecial !== SpecialStatus.UNMONITORED) {
                if (!bar.criticality?.id || !bar.criticality?.name.length || !bar.criticality?.level.length) {
                    barrierErrors.push(i18n('Criticality'))
                }
                if (!bar.updateModeCode) {
                    barrierErrors.push(i18n('update mode code'))
                } else {
                    const errorsArray = updateModeFrontAPI.getValidationForUpdateMode(bar.updateModeCode, bar)
                    barrierErrors.push(...errorsArray)
                }
            }

            if (barrierErrors.length) {
                items.push({
                    type: bar === barrier ? 'BARRIER' : 'ESCALATIONBARRIER',
                    item: bar,
                    name: bar.name,

                    errors: `${i18n('Missing')} ${barrierErrors.join(', ')}`
                })
            }

            if (bar.escalations) {
                const escalationNameSet = new Set()
                for (const escalation of bar.escalations) {
                    if (escalation) {
                        const escalationErrors: string[] = []

                        if (!escalation.name?.length) {
                            escalationErrors.push(i18n('name'))
                        }
                        if (escalationNameSet.has(escalation.name)) {
                            escalationErrors.push(i18n('name (should not be equal)'))
                        }
                        escalationNameSet.add(escalation.name)

                        if (!escalation.barriers?.length) {
                            escalationErrors.push(i18n('at least one barrier'))
                        }

                        if (escalationErrors.length) {
                            items.push({
                                item: escalation as IEditorEscalation,
                                name: escalation.name,
                                type: 'ESCALATIONFACTOR',
                                errors: `Missing ${escalationErrors.join(', ')}`
                            })
                        }

                        if (escalation.barriers) {
                            for (const escalationBarrier of escalation.barriers) {
                                if (escalationBarrier) {
                                    items = [...items, ...mountErrorList(escalationBarrier as IEditorBarrier)]
                                }
                            }
                        }
                    }
                }
            }
        }

        return items
    }

    const { params, setParams } = useContext<IBowtieSettingsRouteContext<IQueryParamsEdit>>(BowtieSettingsRouteContext)

    function addFatherReferenceToBarrier(actualReference: IEditorBarrier): void {
        actualReference.escalations?.forEach((esc: IEditorEscalation) => {
            esc.getFather = actualReference
            esc.barriers?.forEach((bar: IEditorBarrier) => {
                bar.getFather = esc
                addFatherReferenceToBarrier(bar)
            })
        })
    }

    function removeFatherReferenceOfBarrier(actualReference: IEditorBarrier): IEditorBarrier {
        return {
            ...actualReference,
            getFather: undefined,
            escalations: actualReference.escalations.map(esc => ({
                ...esc,
                getFather: undefined,
                barriers: esc.barriers.map(removeFatherReferenceOfBarrier)
            }))
        }
    }

    const handleGetBarrier = async (id: number): Promise<void> => {
        setInLoading(true)

        try {
            const data = await getBarrier(id)
            addFatherReferenceToBarrier(data as IEditorBarrier)
            setBarrier(data as IEditorBarrier)
        } catch (err) {
            setParams({ type: 'list', tab: 'barriers' })
            globalNotification.error(err.message ?? i18n('An error occurred on get barrier.'))
        }

        setInLoading(false)
    }

    const handleOpenEntityModal = (k: IEditorBarrier | IEditorEscalation, type: IBarrierTypes): void => {
        if (type === 'ESCALATIONBARRIER') {
            ;(k as IEditorBarrier).asset = barrier.asset
            const barrierPointers: IEditorBarrier[] = []
            handleRecursiveSearch(k as IEditorBarrier, barrier, barrierPointers)
            setTempItem(barrierPointers)
        } else {
            setTempItem([k] as IEditorBarrier[] | IEditorEscalation[])
        }
        setFocus(k)
        setTypeToEdit(type)
        setShowEntityModal(true)
    }

    const handleCloseEntityModal = (): void => {
        setTempItem(null)
        setFocus(null)
        setTypeToEdit(null)
        setShowEntityModal(false)
    }

    const handleRecursiveSearch = (
        mainBarrier: IEditorBarrier,
        actualBarrier: IEditorBarrier,
        k?: IEditorBarrier[]
    ): void => {
        if (barrier.escalations) {
            actualBarrier.escalations?.forEach(el => {
                el.barriers?.forEach((bar: IEditorBarrier) => {
                    if (bar.id) {
                        if (bar.id === mainBarrier.id) {
                            k.push(bar)
                        }
                    } else {
                        if (bar.uid === mainBarrier.uid) {
                            k.push(bar)
                        }
                    }
                    handleRecursiveSearch(mainBarrier, bar, k)
                })
            })
        }
    }

    const handleRecursiveSearchFromEscalation = (
        referenceEscalation: IEditorEscalation,
        actualBarrier: IEditorBarrier,
        k: IEditorEscalation[]
    ): void => {
        actualBarrier.escalations?.forEach((el: IEditorEscalation) => {
            if (el.id) {
                if (el.id === referenceEscalation.id) {
                    k.push(el)
                }
            } else {
                if (el.uid === referenceEscalation.uid) {
                    k.push(el)
                }
            }
            el.barriers?.forEach((bar: IEditorBarrier) => {
                handleRecursiveSearchFromEscalation(referenceEscalation, bar, k)
            })
        })
    }

    const handleGetRecursiveSearch = (bar: IEditorBarrier): IEditorBarrier[] => {
        const barrierPointers: IEditorBarrier[] = []
        handleRecursiveSearch(bar, barrier, barrierPointers)

        return barrierPointers
    }

    const handleGetRecursiveSearchFromEscalation = (esc: IEditorEscalation): IEditorEscalation[] => {
        const escalationFactorsPointers: IEditorEscalation[] = []
        handleRecursiveSearchFromEscalation(esc, barrier, escalationFactorsPointers)

        return escalationFactorsPointers
    }

    const handleGetUpdateModeMethods = async (): Promise<void> => {
        try {
            const response = await getUpdateModeMethods()
            const newOptionArray = response.data.map(el => {
                return {
                    id: el.id,
                    value: el.method,
                    label: capitalize(el.method.toString())
                }
            })

            setUpdateModeMethods(newOptionArray)
        } catch (err) {
            globalNotification.error(err.message ?? i18n('An error occurred on get update mode methods.'))
        }
    }

    const handleSave = async (): Promise<void> => {
        try {
            setSaveLoading(true)
            const barForSave = { ...barrier }
            const barrierWithoutGetFather = removeFatherReferenceOfBarrier(barForSave as IEditorBarrier)
            const updatedBarrier = await updateBarrier(barrierWithoutGetFather)
            globalNotification.success(i18n('Barrier saved successfully.'))
            setNeedToUpdate(false)
        } catch (err) {
            globalNotification.error(err.message || i18n('Error on save barrier.'))
        } finally {
            setSaveLoading(false)
        }
    }

    const handleBarrierChange = (): void => {
        // const newBarrier = _.cloneDeep(barrier)
        const newBarrier = { ...barrier }

        // const newBarrier = JSON.parse(JSON.stringify(barrier)) as IEditorBarrier

        setBarrier(newBarrier)

        if (tempItem && tempItem[0] === barrier) {
            setTempItem([newBarrier])
        }
        setNeedToUpdate(true)
    }

    const recursiveSearchAndChangeMethod = (barrier: IEditorBarrier, method: IMethod): void => {
        barrier.method = method
        if (barrier.escalations) {
            barrier.escalations.forEach(esc => {
                if (esc.barriers) {
                    esc.barriers.forEach(bar => recursiveSearchAndChangeMethod(bar as IEditorBarrier, method))
                }
            })
        }
    }
    const handleMethodChange = (): void => {
        const newBarrier = { ...barrier }
        recursiveSearchAndChangeMethod(newBarrier, newBarrier.method)
        setBarrier(newBarrier)

        if (tempItem && tempItem[0] === barrier) {
            setTempItem([newBarrier])
        }

        setNeedToUpdate(true)
    }

    const handleGoBack = (): void => {
        const queryString = localStorage.getItem(barrierEditorQueryStringKey)
        let queryParams: IQueryParamsList = { type: 'list', tab: 'barriers' }
        if (queryString != null && queryString.length > 0) {
            try {
                queryParams = JSON.parse(queryString)
            } catch (error) {
                queryParams = { type: 'list', tab: 'barriers', filterBy: barrier?.asset?.id }
            }
        }
        setParams(queryParams)
    }

    useEffect(() => {
        if (params.id) {
            handleGetBarrier(params.id)
        }
    }, [params.id]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!inLoading && (!barrier || barrier.root === false)) {
            setParams({ type: 'list', tab: 'barriers' })
        }
    }, [inLoading]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setErrors(mountErrorList(barrier))
    }, [barrier]) // eslint-disable-line react-hooks/exhaustive-deps

    useLayoutEffect(() => {
        handleGetUpdateModeMethods()
    }, [])

    useEffect(() => {
        handleFetchBarrierTypeIconsAndStoreIntoDictionary()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <EditorTopBar
                title={[
                    {
                        text: barrier?.asset?.name ?? 'RESOURCE NAME',
                        maxWidth: '15%'
                    },
                    {
                        text: barrier?.name ?? 'BARRIER NAME',
                        maxWidth: '75%'
                    }
                ]}
                handleGoBack={() => {
                    if (needToUpdate) {
                        globalConfirmation.warn(
                            i18n('Are you sure you want to leave without saving your work?'),
                            handleGoBack,
                            {
                                btnText: i18n('Leave without saving')
                            }
                        )
                    } else {
                        handleGoBack()
                    }
                }}
                disabled={!!(errors && errors.length) || !needToUpdate}
                handleSave={handleSave}
                saveLoading={saveLoading}
            />
            <BarrierEditorDiv>
                <div className="barrier-template">
                    <BarrierEditorSvg
                        height={dimensions.height - 10}
                        width={dimensions.width - 410}
                        barrierData={barrier}
                        handleBarrierChange={handleBarrierChange}
                        handleEditBarrier={handleOpenEntityModal}
                        editingItem={focus}
                        handleGetRecursiveSearch={handleGetRecursiveSearch}
                        handleGetRecursiveSearchFromEscalation={handleGetRecursiveSearchFromEscalation}
                        barrierTypeIconMap={barrierTypeIconMap}
                    />
                </div>
                <div className="barrier-list">
                    <BarrierEditorList
                        barrier={barrier}
                        errors={errors}
                        handleEditBarrier={(k, type) => {
                            handleOpenEntityModal(k, type)
                            setTempItem([k] as IEditorBarrier[] | IEditorEscalation[])
                            setTypeToEdit(type)
                        }}
                        onListDeleteClick={handleBarrierChange}
                    />
                </div>
                <BarrierEditModal
                    isOpen={(typeToEdit === 'BARRIER' || typeToEdit === 'ESCALATIONBARRIER') && showEntityModal}
                    borderColor={bowtieTheme.barrierColors.active}
                    handleChange={handleBarrierChange}
                    handleMethodChange={handleMethodChange}
                    handleClose={() => handleCloseEntityModal()}
                    item={tempItem as IEditorBarrier[]}
                    handleGetRecursiveSearch={handleGetRecursiveSearch}
                    title={typeToEdit === 'BARRIER' ? 'BARRIER' : 'CONTROL BARRIER'}
                    updateModeMethods={updateModeMethods}
                    defaultMethod={barrier?.method}
                    barrierTypeIconMap={barrierTypeIconMap}
                />
                <EscalationEditModal
                    isOpen={typeToEdit === 'ESCALATIONFACTOR' && showEntityModal}
                    borderColor={bowtieTheme.escalation}
                    handleChange={handleBarrierChange}
                    handleClose={handleCloseEntityModal}
                    item={tempItem as IEditorEscalation[]}
                    title="ESCALATION FACTOR"
                />
            </BarrierEditorDiv>
        </>
    )
}
